import { ChangeEvent } from 'react';

import {
    ChevronDownIcon,
    Controller,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';

import { DebtLabelEnum } from '@src/app/formModels/eligibility/debtCon';
import { Declaration } from '@src/app/interfaces/debtcon';
import DebtConCallOut from '@src/ui/components/CallOut/DebtConCallOut';
import SelectBox from '@src/ui/components/SelectBox/SelectBox';

interface CommonQuestionsProps {
    formItems: any;
    values: any;
    touched: any;
    setTouched: (touched: any, shouldValidate?: boolean) => void;
    setFieldValue: (field: string, value: any) => void;
    handleChange: (e: ChangeEvent<any>) => void;
    handleBlur: (e: ChangeEvent<any>) => void;
    getError: (field: string) => string | undefined;
}

const CommonQuestions = ({
    formItems,
    values,
    touched,
    setTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    getError,
}: CommonQuestionsProps) => {
    const shouldShowInterestRateField =
        values.type !== DebtLabelEnum.CreditCard &&
        values.type !== DebtLabelEnum.ShoppingAccount;

    const getCallOutText = (debtType?: string): string | undefined => {
        switch (debtType) {
            case DebtLabelEnum.PersonalLoan:
                return "It's usually around 9% for a personal loan.";
            case DebtLabelEnum.Overdraft:
                return "It's usually around 36% for an overdraft.";
            case DebtLabelEnum.Mortgage:
                return "It's usually around 5% for a mortgage.";
            case DebtLabelEnum.SecuredLoan:
                return "It's usually around 5% for a secured loan.";
            default:
                return undefined;
        }
    };

    if (!values.startMonth && !values.startYear) {
        values.startMonth = '';
        values.startYear = '';
    }

    if (values.startDate) {
        values.startMonth = values.startDate.split('/')[1];
        values.startYear = values.startDate.split('/')[2];
    }

    return (
        <>
            <SelectBox
                labelSize='small'
                label="Who's the provider?"
                items={formItems.lenderName.items}
                suffix={<ChevronDownIcon />}
                name='lenderName'
                validState={!!values.lenderName}
                error={getError('lenderName')}
                value={values.lenderName || ' '}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('lenderName', e.currentTarget.value);
                }}
                onBlur={handleBlur}
                search
            />
            {values.declaration === Declaration.User && (
                <>
                    <Controller label='Start date'>
                        <div className='grid grid-cols-1 gap-2 grid-cols-2'>
                            <SelectBox
                                items={formItems.month.items}
                                suffix={<ChevronDownIcon />}
                                label='Month'
                                labelSize='small'
                                name='startMonth'
                                placeholder='Select'
                                validState={!!values.startMonth}
                                value={values.startMonth?.toString() || ''}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const startDate = `01/${e.currentTarget.value}/${values.startYear}`;

                                    setTouched({
                                        ...touched,
                                        [`${values.startMonth}}`]: true,
                                    });
                                    setFieldValue('startDate', startDate);
                                    setFieldValue(
                                        'startMonth',
                                        `${e.currentTarget.value}`,
                                    );
                                }}
                                error={getError('startMonth')}
                                onBlur={() => {
                                    return setTouched({
                                        ...touched,
                                        [`${values.startMonth}`]: true,
                                    });
                                }}
                            />
                            <SelectBox
                                items={formItems.year.items}
                                suffix={<ChevronDownIcon />}
                                label='Year'
                                labelSize='small'
                                name='startYear'
                                placeholder='Select'
                                validState={!!values.startYear}
                                value={values.startYear?.toString() || ''}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    const startDate = `01/${values.startMonth}/${e.currentTarget.value}`;

                                    setTouched({
                                        ...touched,
                                        [`${values.startYear}}`]: true,
                                    });
                                    setFieldValue('startDate', startDate);
                                    setFieldValue(
                                        'startYear',
                                        `${e.currentTarget.value}`,
                                    );
                                }}
                                error={getError('startYear')}
                                onBlur={() => {
                                    return setTouched({
                                        ...touched,
                                        [`${values.startYear}`]: true,
                                    });
                                }}
                            />
                        </div>
                    </Controller>
                    {values.startMonth &&
                        values.startYear &&
                        getError('startDate') && (
                            <div className='text-error-100 text-md -mt-5 inline'>
                                {getError('startDate')}
                            </div>
                        )}
                </>
            )}
            <TextField
                labelSize='small'
                label="What's your current balance?"
                prefix={<StirlingIcon />}
                maxLength={10}
                mask={'currency' as any}
                name='amountToConsolidate'
                value={values.amountToConsolidate?.toString() || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
                onBlur={handleBlur}
                error={getError('amountToConsolidate')}
            />
            <TextField
                labelSize='small'
                label='How much do you pay monthly?'
                prefix={<StirlingIcon />}
                maxLength={10}
                mask={'currency' as any}
                name='monthlyPaymentCustomerVerified'
                value={values.monthlyPaymentCustomerVerified?.toString()}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
                onBlur={handleBlur}
                error={getError('monthlyPaymentCustomerVerified')}
            />
            {shouldShowInterestRateField && (
                <>
                    <Controller
                        label="What's the interest rate?"
                        labelSize='small'
                    >
                        <TextField
                            className='w-2/3'
                            suffix='%'
                            maxLength={5}
                            mask='percentage'
                            name='interestRate'
                            value={values.interestRate?.toString() || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={getError('interestRate')}
                        />
                    </Controller>
                    <DebtConCallOut
                        classes='-mt-4'
                        text={getCallOutText(values.type)}
                    />
                </>
            )}
        </>
    );
};

export default CommonQuestions;
